import React, {useState, useEffect, JSX} from 'react';
import {Linking, Platform, StyleSheet} from 'react-native';
import {Text, Heading, Box, HStack, StatusBar, Pressable} from 'native-base';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useContextState as userContext} from '../../../context/user';
import {message} from './../../../utils/messages';
import {LogoutIcon, NotificationsIcon, ProfileIcon} from './../../icons';
import {INotification} from '../../../interfaces/dtos';
import {CallCenterItem, SynchronizationItem} from '../../dashboard';
import useToast from '../../../hooks';
import {AdvertenceDialog} from '../../modals';
import {GoBackButton} from '../../buttons';
import {IBackButton} from '../../../interfaces/ui';
import LocalizationItem from '../../dashboard/LocalizationItem';

interface Props {
  title: string;
  summary: string;
  children: JSX.Element[] | JSX.Element;
  notifications?: INotification[];
  onTapNotifications?: () => void;
  onTapLogout?: () => void;
  onTapProfile?: () => void;
  onTapLocalization?: () => void;
  onTapSynchronization?: () => void;
  isActiveLocalization?: boolean;
  hasLocalization?: boolean;
  canCallCenter?: boolean;
  backButton?: IBackButton;
  hasSynchronization?: boolean;
}

const baseColor = '#06355e';
const beginColor = '#0a62b5';
const endColor = baseColor;

const DashboardTemplate = (props: Props) => {
  const {
    children,
    title,
    summary,
    notifications,
    onTapNotifications,
    onTapLogout,
    onTapProfile,
    onTapSynchronization,
    onTapLocalization: handleTapLocalization,
    canCallCenter,
    backButton,
    isActiveLocalization,
    hasLocalization,
    hasSynchronization,
  } = props;

  const {phone, whatsApp: whatsAppUri} = userContext();
  const {showWhatsAppError} = useToast();
  const showAlerDialogState = useState(false);
  const [localizationIsOn, setLocalizationIsOn] = useState(false);
  const setALertDialog = showAlerDialogState[1];

  useEffect(() => {
    setLocalizationIsOn(isActiveLocalization ?? false);
  }, [isActiveLocalization]);

  const hasUnread = (): boolean => {
    return (notifications && notifications.some(n => n.unread)) ?? false;
  };

  const handleOnTapSynchronization = () => {
    if (onTapSynchronization) {
      onTapSynchronization();
    }
  };

  const handleTapProfile = () => {
    if (onTapProfile) {
      onTapProfile();
    }
  };

  const handleTapNotifications = () => {
    if (onTapNotifications) {
      onTapNotifications();
    }
  };

  const handleLogout = () => {
    setALertDialog(true);
  };

  const showCallCenterButton = (): boolean => {
    return canCallCenter ?? false;
  };

  const showLocalizationButton = (): boolean => {
    return !(Platform.OS === 'web') && (hasLocalization ?? false);
  };

  const showSynchronizationButton = (): boolean => {
    return hasSynchronization ?? false;
  };

  const handleCallPhone = async () => {
    let phoneNumber = '';
    if (Platform.OS === 'android') {
      phoneNumber = `tel:${phone}`;
    } else {
      phoneNumber = `tel://${phone}`;
    }
    try {
      await Linking.openURL(phoneNumber);
    } catch {}
  };

  const handleCallWhatsapp = async () => {
    if (isWhatsAppValid()) {
      try {
        let url = `${whatsAppUri}`;
        await await Linking.openURL(url);
      } catch (ex) {
        showWhatsAppError();
      }
    }
  };

  const isWhatsAppValid = (): boolean => {
    return whatsAppUri !== null && `${whatsAppUri}`.trim() !== '';
  };

  const handleConfirmEndSession = () => {
    if (onTapLogout) {
      onTapLogout();
    }
  };

  const handleBack = () => {
    if (backButton) {
      backButton.onPress();
    }
  };
  const showNotificationsButton = () => {
    if (onTapNotifications) {
      return true;
    }
    return false;
  };

  const showProfileButton = () => {
    if (onTapProfile) {
      return true;
    }
    return false;
  };

  const showLogoutButton = () => {
    if (onTapLogout) {
      return true;
    }
    return false;
  };

  const handleLocalization = async () => {
    if (handleTapLocalization) {
      await handleTapLocalization();
    }
  };

  return (
    <SafeAreaView style={styles.safeAreaView}>
      <StatusBar
        translucent
        backgroundColor="transparent"
        barStyle="light-content"
      />
      <AdvertenceDialog
        title="advertend-dialog-title-default"
        showingState={showAlerDialogState}
        onConfirm={handleConfirmEndSession}
        message="advertend-dialog-message-default"
      />
      <Box flex="1" backgroundColor={baseColor}>
        <Box
          bg={{
            linearGradient: {
              colors: [beginColor, endColor],
              start: [0, 1],
              end: [0, 0],
            },
          }}
          style={styles.imageBackground}
          _text={{fontSize: 'md', fontWeight: 'bold', color: 'white'}}>
          {backButton && (
            <Box>
              <GoBackButton
                label={backButton.label}
                usage="dashboard"
                onPress={() => handleBack()}
              />
            </Box>
          )}
          <HStack style={styles.header}>
            <Box flex={1}>
              <Heading color={'white'} marginX="5" paddingBottom="0">
                {message(title)}
              </Heading>
              <Text color={'white'} marginX="5">
                {message(summary)}
              </Text>
            </Box>
            <HStack paddingRight={'14'} paddingTop={2}>
              {showNotificationsButton() && (
                <Pressable onPress={() => handleTapNotifications()}>
                  <NotificationsIcon unread={hasUnread()} />
                </Pressable>
              )}
              {showProfileButton() && (
                <Pressable onPress={() => handleTapProfile()}>
                  <Box marginLeft={15}>
                    <ProfileIcon />
                  </Box>
                </Pressable>
              )}
              {showLogoutButton() && (
                <Pressable onPress={() => handleLogout()}>
                  <LogoutIcon />
                </Pressable>
              )}
            </HStack>
          </HStack>
        </Box>

        <Box backgroundColor="white" flex="1">
          <Box flex="1">{children}</Box>
          {showCallCenterButton() && (
            <CallCenterItem
              label="Ejecutivo en Línea"
              summary="Comuníquese vía telefónica"
              onCallPhone={handleCallPhone}
              onCallWhatsapp={handleCallWhatsapp}
              whatsappActive={isWhatsAppValid()}
            />
          )}
          {showLocalizationButton() && (
            <LocalizationItem
              onTap={handleLocalization}
              switchedOn={localizationIsOn}
            />
          )}
          {showSynchronizationButton() && (
            <SynchronizationItem onTap={handleOnTapSynchronization} />
          )}
        </Box>
      </Box>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeAreaView: {
    backgroundColor: '#06355e',
    flex: 1,
  },
  imageBackground: {
    width: '100%',
    paddingTop: 20,
  },
  header: {
    height: 90,
  },
});

export default DashboardTemplate;
